import {
  useCallback, useEffect, useState,
} from 'react'
import {
  getApiErrorMessage,
  getApiErrorStatus,
  referralService,
} from '../services/api'
import type {
  SuggestedContact,
} from '../client'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  copyTextToClipboard,
} from '../utils/clipboard'
import {
  EMAIL_RE,
  mergeInvited,
} from '../components/referralShared'
import type {
  InvitedContact,
} from '../components/referralShared'

// Shown if a 429 somehow arrives without the backend's own detail string.
// Exported so the page can reuse it as the banner's fallback (one source).
export const DAILY_LIMIT_FALLBACK
  = "You've reached today's invite limit. You can invite more people tomorrow."

interface UseReferralInvitesOptions {
  /** PostHog event fired when a one-click invite to a listed contact is sent
   * (differs per surface so the strip and the page funnel separately). */
  contactInviteEvent: string
  /** Drop a just-invited person from the surface's active list (suggestions on
   * the strip, the network list on the page). */
  removeFromList: (email: string) => void
  /** Sending surface + programme passed to the backend, which re-derives the
   * reward recipient and the invite copy from it. 'refer' is the Brand
   * Ambassador programme (grants the invitee's $200, investor copy),
   * 'refer_standard' is the /refer page's default, omitted → the strip. Both
   * of the latter two follow the standard programme. */
  source?: string
}

/**
 * Shared invite state + handlers for the referral surfaces (ReferralStrip and
 * ReferralPage): the persisted invited list with engagement statuses (re-pulled
 * on tab focus, since opens/clicks land out-of-band), per-contact send state,
 * the free-form email invite, and link copying. Keeps the two surfaces'
 * behavior identical so a fix in one can't silently miss the other.
 */
export function useReferralInvites({
  contactInviteEvent,
  removeFromList,
  source,
}: UseReferralInvitesOptions) {
  // People already invited — seeded from the backend so the status persists
  // across reloads, then kept in sync optimistically as new invites are sent.
  const [invited, setInvited] = useState<InvitedContact[]>([])
  const [sending, setSending] = useState<Set<string>>(new Set())
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Flips true once the backend rejects a send with the per-user daily cap
  // (HTTP 429). Surfaces used to gate further one-click sends for the day and
  // show the backend's friendly explanation instead of a generic error.
  const [dailyLimitReached, setDailyLimitReached] = useState(false)
  const [dailyLimitMessage, setDailyLimitMessage] = useState<string | null>(null)

  // Pull the persisted invites and fold them in. Open/click status updates
  // happen out-of-band (the recipient acts on the email), so we re-pull rather
  // than only seeding once — see the focus/visibility listener below.
  const loadInvites = useCallback(async () => {
    try {
      const items = await referralService.getInvites()
      const incoming: InvitedContact[] = items.map((i) => ({
        email: i.email,
        name: i.name ?? null,
        status: i.status ?? 'INVITED',
      }))
      setInvited((prev) => mergeInvited(prev, incoming))
    } catch {
      // Best-effort: keep whatever's already shown if the refetch fails.
    }
  }, [])

  useEffect(() => {
    void loadInvites()
  }, [loadInvites])

  // Refresh invite status when the user returns to the tab/window — that's when
  // a recipient's open/click is most likely to have landed since last render.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') void loadInvites()
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [loadInvites])

  const setSendingFor = (email: string, on: boolean) => {
    setSending((prev) => {
      const next = new Set(prev)
      if (on) {
        next.add(email)
      } else {
        next.delete(email)
      }
      return next
    })
  }

  // Move a just-invited person into the "Invited" list and out of the surface's
  // active list, so they don't keep showing up as a fresh suggestion. Deduped
  // by email (case-insensitive) against what the backend already returned.
  const markInvited = (email: string, name: string | null) => {
    const key = email.trim().toLowerCase()
    removeFromList(key)
    setInvited((prev) => {
      if (prev.some((c) => c.email.toLowerCase() === key)) return prev
      return [{ email, name, status: 'INVITED' }, ...prev]
    })
  }

  // Turn a failed send into user-facing state. A 429 is the daily cap: latch it
  // so the surface can disable further one-click sends and show the backend's
  // own message; any other failure is a transient per-recipient error.
  const handleSendError = (err: unknown, target: string) => {
    if (getApiErrorStatus(err) === 429) {
      const message = getApiErrorMessage(err, DAILY_LIMIT_FALLBACK)
      setDailyLimitReached(true)
      setDailyLimitMessage(message)
      setError(message)
      return
    }
    setError(getApiErrorMessage(err, `Couldn't send to ${target}. Please try again.`))
  }

  const sendContactInvite = async (contact: SuggestedContact) => {
    if (dailyLimitReached) return
    setError(null)
    setSendingFor(contact.email, true)
    try {
      await referralService.sendInvite(contact.email, contact.name, source)
      markInvited(contact.email, contact.name)
      trackEvent(contactInviteEvent)
    } catch (err) {
      handleSendError(err, contact.email)
    } finally {
      setSendingFor(contact.email, false)
    }
  }

  const sendFreeformInvite = async () => {
    if (dailyLimitReached) return
    const email = inviteEmail.trim()
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address.')
      return
    }
    setError(null)
    setInviteSending(true)
    try {
      await referralService.sendInvite(email, null, source)
      markInvited(email, null)
      trackEvent(OnboardingEvents.REFERRAL_INVITE_SENT)
      setInviteEmail('')
    } catch (err) {
      handleSendError(err, email)
    } finally {
      setInviteSending(false)
    }
  }

  const copyLink = async (url: string) => {
    try {
      await copyTextToClipboard(url)
      setCopied(true)
      trackEvent(OnboardingEvents.REFERRAL_LINK_COPIED)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      setError('Couldn’t copy the link — long-press to copy it manually.')
    }
  }

  return {
    invited,
    sending,
    inviteEmail,
    setInviteEmail,
    inviteSending,
    copied,
    error,
    dailyLimitReached,
    dailyLimitMessage,
    sendContactInvite,
    sendFreeformInvite,
    copyLink,
  }
}
